import { defineStore } from 'pinia';
import KeycloakService from '@/services/keycloakService';
import { getApiUrl } from '@/utils/url';
import type { GetRandomTipDto, RandomTipResponseDto, RandomTipApiResponse } from '@/types/api';

export const useTipStore = defineStore('tipStore', {
  state: () => ({
    tip: null as RandomTipResponseDto | null,
    loading: false,
    error: null as string | null,
    currentLang: 'fr',
  }),

  getters: {
    tipBody(state): string | null {
      if (!state.tip) return null;

      const normalizeQuotes = (txt: string) => txt?.replace(/""/g, '"') ?? txt;

      const lang = state.currentLang.split('-')[0];
      const key = `tip_body_${lang}` as keyof RandomTipResponseDto;

      if (state.tip[key] && typeof state.tip[key] === 'string') {
        return normalizeQuotes(state.tip[key] as string);
      }
      if (state.tip.tip_body_en) return normalizeQuotes(state.tip.tip_body_en);
      return normalizeQuotes(state.tip.tip_body_fr ?? '');
    },
  },

  actions: {
    setLanguage(lang: string) {
      this.currentLang = lang;
    },

    /** POST /tip/random with GetRandomTipDto → { tip: RandomTipResponseDto } */
    async loadRandomTip(products: string[], realm?: string) {
      this.loading = true;
      this.error = null;

      try {
        await KeycloakService.CallUpdate();
        const body: GetRandomTipDto = { products, realm };
        const res = await fetch(`${getApiUrl()}/tip/random`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${KeycloakService.token()}`,
          },
          body: JSON.stringify(body),
        });

        const data: RandomTipApiResponse & { message?: string } = await res.json();
        if (!res.ok) throw new Error(data.message ?? 'Error loading tip');

        this.tip = data.tip;
      } catch (e: unknown) {
        this.error = e instanceof Error ? e.message : 'Error loading tip';
      } finally {
        this.loading = false;
      }
    },
  },
});
